import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import TableDataCell from './TableDataCell';

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  border: 0px;
  border-radius: 0px;
  border-color: transparent;
  background-color: transparent;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  padding: ${(props) => props.padding}px;
  border: 1px solid transparent;

  &:hover, &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }

`;

class TableFieldSelectionCell extends Component {

  static propTypes ={
    id: PropTypes.string,
    disabled: PropTypes.bool,
    focused: PropTypes.bool,
    rowId: PropTypes.string,
    columnId: PropTypes.string,
    cellKey: PropTypes.string,
    value: PropTypes.string,
    options: PropTypes.array,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    onSelectRef: PropTypes.func, //Used only for testing

    //Styles
    primaryColor: PropTypes.string,
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    borderWidth: PropTypes.number,
    padding: PropTypes.number,
    width: PropTypes.number,

  }

  shouldComponentUpdate(nextProps) {

    const {
      focused,
      value,
      options,
      disabled,
      width,
      borderColor,
      borderWidth,
      padding,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,

    } = this.props;

    if (
      !isEqual(options, nextProps.options)
      || value !== nextProps.value
      || disabled !== nextProps.disabled
      || focused !== nextProps.focused
      || width !== nextProps.width
      || borderColor !== nextProps.borderColor
      || borderWidth !== nextProps.borderWidth
      || padding !== nextProps.padding
      || fontSize !== nextProps.fontSize
      || fontColor !== nextProps.fontColor
      || fontStyle !== nextProps.fontStyle
      || fontWeight !== nextProps.fontWeight
      || textAlign !== nextProps.textAlign
      || textTransform !== nextProps.textTransform
      || textDecoration !== nextProps.textDecoration
      || textOverflow !== nextProps.textOverflow
    ) {
      return true;
    } else {
      return false;
    }

  }

  render() {

    const {
      id,
      rowId,
      columnId,
      disabled,
      cellKey,
      focused,
      onFocus,
      onKeyPress,
      onSelectRef,

      primaryColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      padding,
      width,
      value,
      backgroundColor,
      invalid,

    } = this.props;


    return focused && !disabled ? (
      <TableDataCell
        key={cellKey}
        data-testid={`${id}-${rowId}-${columnId}-selection-cell`}
        {...{
          focused,
          width,
          padding: 0,
          borderColor,
          borderWidth,
          primaryColor,
          backgroundColor,
        }}
      >
        <StyledSelect
          autoFocus
          value={value || ''}
          ref={(e) => {
            if (!e) return;
            if (onSelectRef) onSelectRef(e);
            e.focus();
          }}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onKeyDown={(e) => {
            if (onKeyPress) onKeyPress(rowId, columnId, e);
          }}
          {...{
            primaryColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            padding,
            backgroundColor
          }}
        >
          <option key="empty" value="" />
          {this.getOptions()}
        </StyledSelect>
      </TableDataCell>
    ) : (
      <TableDataCell
        key={cellKey}
        disabled={disabled}
        data-testid={`${id}-${rowId}-${columnId}-selection-cell`}
        handleCellRef={(e) => {
          /**
           * Used to make sure the rendered textarea cell
           * is the same size as the display text when
           * the text cell is focused.
           */
          if (e) {
            this.height = e.offsetHeight;
            this.width = e.offsetWidth;
          }
        }}
        onClick={() => {
          if (!disabled && !focused) onFocus(rowId, columnId);
        }}
        {...{
          width,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          textOverflow,
          borderColor,
          borderWidth,
          padding,
          primaryColor,
          backgroundColor,
          invalid,
        }}
      >
        {this.getDefaultDisplayValue()}
      </TableDataCell>
    );

  }

  getOptions = () => {

    const { options } = this.props;

    const dropdownOptions = [];

    options.forEach((opt) => {
      if (opt.deleted) return;

      dropdownOptions.push((
        <option key={opt._id} value={opt._id}>
          {opt.value}
        </option>
      ));
    });

    return dropdownOptions;

  }

  getDefaultDisplayValue = () => {

    const { options, value } = this.props;

    /**
     * If selected option is deleted then return empty selection placeholder
     */
    const activeCellOption = value ?
      options.find((option) => {
        return option._id === value && !option.deleted;
      }) : null;

    return activeCellOption ? activeCellOption.value : '';

  }

  handleChange = (e) => {

    const { onChange, rowId, columnId, value } = this.props;
    const nextValue = e.target.value;

    if (onChange && nextValue !== value) {
      onChange(rowId, columnId, nextValue);
    }

  }

  handleFocus = (e) => {

    const { onFocus, rowId, columnId, columnIdentifier, columnType } = this.props;

    if (onFocus) {
      onFocus(rowId, columnId, columnIdentifier, columnType, { blur: () => { e.target.blur(); } });
    }
  };

  handleBlur = (e) => {

    const { onBlur, rowId, columnId, columnIdentifier, value, onChange } = this.props;

    const nextValue = e.target.value;

    // We only need to fire onChange when nextValue is not null
    if (onChange && nextValue && nextValue !== value) {
      onChange(rowId, columnId, nextValue);
    }

    if (onBlur) onBlur(rowId, columnId, columnIdentifier, e);

  }

}

export default TableFieldSelectionCell;
